import { useEffect, useMemo, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import type { Integration, IntegrationOAuthSecret } from "@prescreen/lib/types"
import {
  type BankingCapabilities,
  type BankingPermissionTier,
} from "@prescreen/lib/types"
import { Loader2 } from "lucide-react"
import { TooltipProvider } from "@/components/ui/tooltip"
import { SecretPermissionsForm } from "@/components/SecretPermissionsForm"
import {
  buildSecretGrantedScopesFromSelections,
  DEFAULT_BANKING_CAPABILITIES,
  deriveSecretFormStateFromGrantedScopes,
  integrationHasConfigurableOAuthSecrets,
  secretFormSelectionsMeetMinimum,
} from "@/lib/oauth-secret-scope-builder"

export type EditIntegrationSecretDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  integration: Integration
  secret: IntegrationOAuthSecret | null
  onSubmit: (payload: {
    name: string
    description?: string
    grantedScopes: string[]
  }) => Promise<void>
  isSubmitting?: boolean
}

export function EditIntegrationSecretDialog({
  open,
  onOpenChange,
  integration,
  secret,
  onSubmit,
  isSubmitting = false,
}: EditIntegrationSecretDialogProps) {
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [capabilities, setCapabilities] = useState<BankingCapabilities | null>(null)
  const [synergyDocumentsTier, setSynergyDocumentsTier] =
    useState<BankingPermissionTier>("none")
  const [prequalTier, setPrequalTier] = useState<BankingPermissionTier>("none")
  const [hardpullTier, setHardpullTier] = useState<BankingPermissionTier>("none")
  const [submitting, setSubmitting] = useState(false)

  const hasConfigurableSecrets = integrationHasConfigurableOAuthSecrets(integration)

  const grantedScopesSignature = useMemo(
    () => (secret?.grantedScopes ?? []).slice().sort().join("\n"),
    [secret?.grantedScopes],
  )

  useEffect(() => {
    if (!open || !secret) {
      return
    }
    setName(secret.name)
    setDescription(secret.description ?? "")
    const derived = deriveSecretFormStateFromGrantedScopes(secret.grantedScopes)
    const caps = derived.capabilities
    setCapabilities({
      ...caps,
      rdcRequests: caps.rdcRequests === "write" ? "read" : caps.rdcRequests,
    })
    setSynergyDocumentsTier(derived.synergyDocumentsTier)
    setPrequalTier(derived.prequalTier)
    setHardpullTier(derived.hardpullTier)
  }, [open, secret?.secretId, secret?.name, secret?.description, grantedScopesSignature])

  const updateTier = (key: keyof BankingCapabilities, tier: BankingPermissionTier) => {
    setCapabilities((prev) =>
      prev
        ? {
            ...DEFAULT_BANKING_CAPABILITIES,
            ...prev,
            [key]: tier === "write" && key === "rdcRequests" ? "read" : tier,
          }
        : prev,
    )
  }

  const scopesValid =
    capabilities !== null &&
    secretFormSelectionsMeetMinimum(
      integration,
      capabilities,
      synergyDocumentsTier,
      prequalTier,
      hardpullTier
    )

  const handleSubmit = async () => {
    const trimmed = name.trim()
    if (!capabilities || !trimmed || !scopesValid) {
      return
    }
    setSubmitting(true)
    try {
      await onSubmit({
        name: trimmed,
        description: description.trim() || undefined,
        grantedScopes: buildSecretGrantedScopesFromSelections(
          integration,
          capabilities,
          synergyDocumentsTier,
          prequalTier,
          hardpullTier,
        ),
      })
      onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  const trimmedName = name.trim()
  const busy = submitting || isSubmitting
  const submitEnabled =
    Boolean(trimmedName) && !busy && hasConfigurableSecrets && scopesValid && capabilities !== null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit secret</DialogTitle>
          <DialogDescription>
            Update the display name, description, and token permissions. Grant at least read access on one permission
            across the sections that apply to this integration.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="edit-secret-name">Secret name</Label>
            <Input
              id="edit-secret-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Production reporting"
              className="mt-1"
              autoComplete="off"
            />
          </div>
          <div>
            <Label htmlFor="edit-secret-desc">Description (optional)</Label>
            <Textarea
              id="edit-secret-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this credential is used for"
              className="mt-1 min-h-[72px]"
            />
          </div>

          {hasConfigurableSecrets && capabilities ? (
            <TooltipProvider delayDuration={200}>
              <SecretPermissionsForm
                idPrefix="edit-secret"
                integration={integration}
                bankingCapabilities={capabilities}
                onBankingTierChange={updateTier}
                synergyDocumentsTier={synergyDocumentsTier}
                onSynergyDocumentsTierChange={setSynergyDocumentsTier}
                prequalTier={prequalTier}
                onPrequalTierChange={setPrequalTier}
                hardpullTier={hardpullTier}
                onHardpullTierChange={setHardpullTier}
              />
            </TooltipProvider>
          ) : hasConfigurableSecrets ? (
            <p className="text-sm text-muted-foreground">Loading permissions…</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Configure Equifax prequalification, Equifax DIT, Jack Henry Silverlake, or Synergy on this integration
              before editing OAuth secret scopes.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" disabled={!submitEnabled} onClick={() => void handleSubmit()}>
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Saving…
              </>
            ) : (
              "Save changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
